import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import styled from 'styled-components';
import Panel from '../../components/outline/Panel';
import { useAuth } from '../../hooks/common/useAuth';
import { PERMISSION_ACTIONS, PERMISSION_SUBJECTS } from '../../constants/service/common/userPermissions';
import { PROMOTION_SCHEDULE_DETAIL_URL_PATH } from '../../constants/service/common/urlConstants';
import { setDetailPageUrl } from '../../lib/common';
import {
  ActiveFilterChip,
  ActiveFilters,
  ListPager,
  ListTable,
  ListToolbar,
  useExcelDownload,
} from '../../components/list';
import { PromotionSchedule, PromotionScheduleListFilter, promotionScheduleApi } from '../../lib/api/promotionSchedule';
import { ScheduleFormModal } from '../../views/promotion/schedule/components/ScheduleFormModal';
import { ScheduleListFilter } from '../../views/promotion/schedule/components/ScheduleListFilter';
import { buildScheduleListColumns } from '../../views/promotion/schedule/components/ScheduleListTable';
import { useScheduleListState } from '../../views/promotion/schedule/hooks/useScheduleListState';
import { promotionScheduleQuery } from '../../views/promotion/schedule/queries';
import { buildScheduleFilterChips } from '../../views/promotion/schedule/utils/listFilterChips';

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 20px;
`;

/**
 * 프로모션 리스트 컨테이너 (AMZUWEB-682).
 *
 * 타임라인이 못 하는 세 가지를 맡는다 — **찾기**(필터·검색·정렬)·**링크 공유**(URL 쿼리)·
 * **엑셀 반출**. 타임라인은 그대로 둔다. 둘은 같은 데이터의 다른 쓰임이다.
 *
 * 상태는 전부 URL 에 있다(`useScheduleListState`). 그래서 이 컴포넌트에는
 * 모달 열림 여부 말고는 로컬 상태가 없다 — 새로고침·뒤로가기·링크 공유가 공짜로 따라온다.
 */
export const PromotionScheduleListContainer = (): React.JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const { can } = useAuth();

  const listState = useScheduleListState();
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const canCreate = can(PERMISSION_ACTIONS.CREATE, PERMISSION_SUBJECTS.PROMOTION_SCHEDULE);
  const canUpdate = can(PERMISSION_ACTIONS.UPDATE, PERMISSION_SUBJECTS.PROMOTION_SCHEDULE);
  const canDelete = can(PERMISSION_ACTIONS.DELETE, PERMISSION_SUBJECTS.PROMOTION_SCHEDULE);
  // 반출은 조회와 별도 권한이다 — 없으면 버튼 자체를 감춘다(눌러서 403 을 보게 하지 않는다)
  const canDownloadExcel = can(PERMISSION_ACTIONS.DOWNLOAD_EXCEL, PERMISSION_SUBJECTS.PROMOTION_SCHEDULE);

  const { data, isFetching, isPreviousData } = useQuery({
    ...promotionScheduleQuery.list(listState.listQuery),
    keepPreviousData: true,
  });

  const rows = data?.promotionSchedules ?? [];
  const totalCount = data?.totalCount ?? 0;

  /**
   * 총건수는 조회를 해봐야 알기에, 응답을 받은 뒤 범위를 넘은 페이지를 되돌린다.
   * `?page=999` 로 들어온 링크나 공유 뒤 데이터가 줄어든 링크에서 빈 표가 나오는 걸 막는다.
   *
   * 판단은 **지금 조건의 응답으로만** 한다. `keepPreviousData` 때문에 화면에는 이전 응답이
   * 남아 있는데, 그 총건수로 클램프하면 조건을 바꾸는 순간 엉뚱한 페이지로 튄다.
   * `isFetching` 만으로는 모자라다 — 조회가 멈춘(오류·오프라인) 동안에도 이전 응답이 남는다.
   */
  useEffect(() => {
    if (!isFetching && !isPreviousData && data) {
      listState.clampPage(totalCount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetching, isPreviousData, data, totalCount, listState.page]);

  /**
   * 반출은 **현재 화면과 같은 조건**으로 나가야 한다 — 페이지·정렬은 빼고 필터만 보낸다.
   * (서버가 이 필터를 리스트 조회와 같은 함수로 조립하므로 파일 건수가 화면 총건수와 같다.)
   */
  const excelFilter = useMemo<PromotionScheduleListFilter>(() => {
    const { from, to, tracks, statuses, types, adminIds, hasEvent, hasCoupon, keyword } = listState.listQuery;
    return { from, to, tracks, statuses, types, adminIds, hasEvent, hasCoupon, keyword };
  }, [listState.listQuery]);

  const { isRequesting, requestExcelDownload } = useExcelDownload({
    request: () => promotionScheduleApi.requestExcelDownload(excelFilter),
  });

  const columns = useMemo(() => buildScheduleListColumns(), []);

  const chips: ActiveFilterChip[] = useMemo(() => buildScheduleFilterChips(listState.query), [listState.query]);

  /**
   * 경로를 문자열로 조립하지 않는다 — 상수 한 곳만 고치면 헤더 숏컷·모달 링크까지 따라온다.
   *
   * 🔴 **지금 보던 리스트 URL 을 들려 보낸다.** 상세의 "리스트로" 가 경로 상수만 쓰면
   * 필터·정렬·페이지가 전부 날아간 맨 리스트로 떨어진다 — 조건을 걸어 찾아 들어온 사람에게
   * 그건 작업을 처음부터 다시 하라는 뜻이다. 이 화면의 상태는 전부 URL 에 있으므로
   * `pathname + search` 만 넘기면 그대로 복원된다.
   */
  const handleClickRow = (row: PromotionSchedule): void => {
    navigate(setDetailPageUrl(PROMOTION_SCHEDULE_DETAIL_URL_PATH, row.id), {
      state: { listUrl: `${location.pathname}${location.search}` },
    });
  };

  return (
    <StyledContainer>
      <ScheduleListFilter
        filter={listState.draft}
        onChange={listState.setDraft}
        onSearch={listState.search}
        onClear={listState.reset}
        activeCount={chips.length}
      />

      <ActiveFilters chips={chips} onClearAll={chips.length > 0 ? listState.reset : undefined} />

      {/*
        표를 Panel 로 감싼다 — 어드민 전역의 리스트 봉투(114곳)라, 없으면 흰 배경 위에
        표만 떠 있어 같은 제품의 다른 화면처럼 보인다.
      */}
      <Panel>
        <div className="panel-body">
          <ListToolbar
            totalCount={totalCount}
            onClickCreate={canCreate ? () => setIsCreateOpen(true) : undefined}
            createText="+ 프로모션 등록"
            onClickExcelDownload={canDownloadExcel ? requestExcelDownload : undefined}
            isExcelDownloading={isRequesting}
          />

          <ListTable<PromotionSchedule>
            rowKey="id"
            columns={columns}
            dataSource={rows}
            isLoading={isFetching}
            onClickRow={handleClickRow}
            sortBy={listState.sortBy}
            sortOrder={listState.sortOrder}
            onChangeSort={listState.setSort}
            scrollX={1600}
          />

          <ListPager
            page={listState.page}
            pageSize={listState.pageSize}
            totalCount={totalCount}
            onChange={listState.setPage}
          />
        </div>
      </Panel>

      {isCreateOpen && (
        <ScheduleFormModal
          schedule={null}
          canUpdate={canUpdate}
          canDelete={canDelete}
          onClose={() => setIsCreateOpen(false)}
        />
      )}
    </StyledContainer>
  );
};
